import { memo, useMemo } from 'react'
import { Box, Button, MultiSelect, Select, Stack } from 'components'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { GradeOption, FilterType } from './useModalState'

interface SearchFiltersProps {
  filterType: FilterType
  filterCategories: string[]
  filterGrades: string[]
  availableCategories: string[]
  availableGrades: GradeOption[]
  onFilterTypeChange: (value: FilterType) => void
  onFilterCategoriesChange: (value: string[]) => void
  onFilterGradesChange: (value: string[]) => void
  onClearFilters: () => void
  hasFilters: boolean
}

const MAX_DROPDOWN_HEIGHT = 200
/** Per the design, Type is half as wide as the two multi-value filters. */
const TYPE_FLEX = 1
const MULTI_FLEX = 2

/* The filters share the full row width and align to the top, so a multi-select
   that stacks its selected values grows downwards on its own. */
const rowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  width: '100%',
} as const

const typeSx = { flex: TYPE_FLEX, minWidth: 0 } as const
const multiSx = { flex: MULTI_FLEX, minWidth: 0 } as const
const clearButtonSx = { alignSelf: 'flex-start' } as const

export const SearchFilters = memo(({
  filterType,
  filterCategories,
  filterGrades,
  availableCategories,
  availableGrades,
  onFilterTypeChange,
  onFilterCategoriesChange,
  onFilterGradesChange,
  onClearFilters,
  hasFilters,
}: SearchFiltersProps) => {
  const { t } = useTranslation()

  const typeOptions = useMemo(() => [
    { value: 'skill', label: t('arc_filter_type_skills') },
    { value: 'content', label: t('arc_filter_type_standards') },
  ], [t])

  const gradeOptions = useMemo(
    () => [...availableGrades]
      .sort((a, b) => a.sequence - b.sequence)
      .map(g => ({ value: g.id, label: g.name })),
    [availableGrades],
  )

  const categoryOptions = useMemo(
    () => availableCategories.map(cat => ({ value: cat, label: cat })),
    [availableCategories],
  )

  const handleTypeChange = (value: string | null) => {
    onFilterTypeChange(value as FilterType)
  }

  return (
    <Stack spacing={8}>
      <Box data-field='searchFilters' sx={rowSx}>
        <Select
          label={t('arc_filter_type')}
          placeholder={t('arc_filter_type_placeholder')}
          value={filterType}
          onChange={handleTypeChange}
          data={typeOptions}
          clearable
          size='xs'
          radius='md'
          withinPortal
          maxDropdownHeight={MAX_DROPDOWN_HEIGHT}
          sx={typeSx}
        />

        {gradeOptions.length > 0 && (
          <MultiSelect
            label={t('arc_filter_grades')}
            placeholder={t('arc_filter_grades_placeholder')}
            value={filterGrades}
            onChange={onFilterGradesChange}
            data={gradeOptions}
            nothingFound={t('arc_no_options')}
            clearable
            searchable
            size='xs'
            radius='md'
            withinPortal
            maxDropdownHeight={MAX_DROPDOWN_HEIGHT}
            sx={multiSx}
          />
        )}

        <MultiSelect
          label={t('arc_filter_categories')}
          placeholder={t('arc_filter_categories_placeholder')}
          value={filterCategories}
          onChange={onFilterCategoriesChange}
          data={categoryOptions}
          nothingFound={t('arc_no_options')}
          clearable
          searchable
          size='xs'
          radius='md'
          withinPortal
          maxDropdownHeight={MAX_DROPDOWN_HEIGHT}
          sx={multiSx}
        />
      </Box>

      {hasFilters && (
        <Button
          variant='outline'
          size='xs'
          radius='xl'
          leftIcon={<IconX size={14} stroke={2} />}
          onClick={onClearFilters}
          sx={clearButtonSx}
        >
          {t('arc_clear_filters')}
        </Button>
      )}
    </Stack>
  )
})

SearchFilters.displayName = 'SearchFilters'
